import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { baseUrl } from '../config';
import { Button } from '../components/Button';
import { useSEO } from '../hooks/useSEO';

export default function VerificationPending() {
  useSEO({ title: 'Verify your email – Compress GLB', robots: 'noindex, follow' });

  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState('');
  
  // Get email from URL params or localStorage
  const email = new URLSearchParams(window.location.search).get('email') || localStorage.getItem('pending_verification_email');

  const handleResendVerification = async () => {
    if (!email) {
      setMessage('Email address not found. Please try signing in again.');
      return;
    }

    setIsResending(true);
    setMessage('');

    try {
      const response = await fetch(`${baseUrl}/api/1/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username: email }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setMessage('Verification email sent! Please check your inbox.');
      } else {
        setMessage(data.message || 'Failed to resend verification email. Please try again.');
      }
    } catch (error) {
      setMessage('An error occurred. Please try again later.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="bg-background w-screen min-h-screen flex justify-center items-center px-4 py-10">
      <div className="bg-background-elevated p-8 md:p-10 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 w-full max-w-md space-y-6 border border-border/60 text-center">
        <header className="space-y-2">
          <div className="mx-auto w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">Verify Your Email</h1>
        </header>
        
        <div className="text-muted-foreground space-y-3">
          <p>
            A verification email has been sent to:
          </p>
          <p className="text-foreground font-medium bg-background-soft px-4 py-2 rounded-lg border border-border/40">
            {email}
          </p>
          <p className="text-sm">
            Please check your inbox and click the verification link to complete your registration.
          </p>
        </div>

        {message && (
          <div className={`text-sm px-4 py-3 rounded-lg border ${
            message.includes('error') || message.includes('Failed') 
              ? 'bg-danger/10 border-danger/20 text-danger' 
              : 'bg-success/10 border-success/20 text-success'
          }`}>
            {message}
          </div>
        )}

        <div className="space-y-3 pt-2">
          <Button
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full"
            size="lg"
          >
            {isResending ? 'Sending...' : 'Resend Verification Email'}
          </Button>

          <Button
            onClick={() => navigate({ to: '/sign-in' })}
            variant="secondary"
            className="w-full"
            size="lg"
          >
            Back to Sign In
          </Button>
        </div>

        <p className="text-xs text-muted-foreground pt-2">
          Didn't receive the email? Check your spam folder or try resending the verification email.
        </p>
      </div>
    </div>
  );
} 